import { 
  collection, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase.config';
import { getCollectionForCompany } from './dataAdapter';

// 💰 Employee Cash Flow Management Utilities
export class EmployeeCashManager {
  static async validateEmployeeIsActive(companyData, employeeId) {
    try {
      if (!employeeId) {
        return { valid: false, error: 'Employee is required' };
      }

      const employeeRef = doc(
        db,
        getCollectionForCompany(db, companyData, 'employees').path,
        employeeId
      );
      const employeeSnap = await getDoc(employeeRef);

      if (!employeeSnap.exists()) {
        return { valid: false, error: 'Employee not found' };
      }

      const employeeData = employeeSnap.data() || {};
      const employeeStatus = (employeeData.status || 'active').toLowerCase();

      if (employeeStatus !== 'active') {
        return {
          valid: false,
          error: `Employee is ${employeeStatus}. Only active employees can receive new transactions.`
        };
      }

      return { valid: true, employee: { id: employeeSnap.id, ...employeeData } };
    } catch (error) {
      console.error('Error validating employee status:', error);
      return { valid: false, error: error.message || 'Failed to validate employee status' };
    }
  }

  
  /**
   * Create cash transaction (Cash or UPI)
   */
  static async createCashTransaction(companyData, transactionData) {
    try {
      const { employeeId, transactionType, amount, paymentMode, ...otherData } = transactionData;

      const activeEmployeeValidation = await this.validateEmployeeIsActive(companyData, employeeId);
      if (!activeEmployeeValidation.valid) {
        return { success: false, error: activeEmployeeValidation.error };
      }
      
      // Validate amount
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return { success: false, error: 'Invalid transaction amount' };
      }
      
      // Get current employee balance
      const employeeBalance = await this.getEmployeeBalance(companyData, employeeId);
      
      console.log('💰 Current employee balance:', {
        employeeId,
        currentBalance: employeeBalance,
        transactionType,
        paymentMode,
        amount: amountNum
      });
      
      // Calculate new balance based on transaction type
      let newBalance = {
        total: employeeBalance.total || 0,
        cash: employeeBalance.cash || 0,
        upi: employeeBalance.upi || 0
      };
      
      if (transactionType === 'inflow') {
        if (paymentMode === 'cash') {
          newBalance.cash += amountNum;
          newBalance.total += amountNum;
        } else if (paymentMode === 'upi') {
          newBalance.upi += amountNum;
          newBalance.total += amountNum;
        }
      } else if (transactionType === 'outflow') {
        if (paymentMode === 'cash') {
          newBalance.cash -= amountNum;
          newBalance.total -= amountNum;
        } else if (paymentMode === 'upi') {
          newBalance.upi -= amountNum;
          newBalance.total -= amountNum;
        }
      }
      
      console.log('💰 Calculated new balance:', {
        employeeId,
        oldBalance: employeeBalance,
        newBalance,
        transactionType,
        paymentMode,
        amount: amountNum
      });
      
      // Validate balance (should not go negative) - only for outflow transactions.
      // Inflow transactions always increase balance, so they can never result in negative balance.
      // Note: Older records may have been created before this check existed; ledger sort is chronological
      // (date + createdAt) so "balance after" column reads correctly when same-day order is inflow then outflow.
      if (transactionType === 'outflow' && (newBalance.cash < 0 || newBalance.upi < 0 || newBalance.total < 0)) {
        console.error('❌ Insufficient balance:', {
          employeeId,
          currentBalance: employeeBalance,
          requestedAmount: amountNum,
          paymentMode,
          wouldResultIn: newBalance
        });
        return { 
          success: false, 
          error: 'Insufficient balance. Cannot process transaction.' 
        };
      }
      
      // Create transaction record
      const transactionRecord = {
        employeeId,
        employeeName: transactionData.employeeName || '',
        transactionType,
        amount: amountNum,
        balance: newBalance.total, // Store total balance
        paymentMode,
        
        // Cash-specific details
        ...(paymentMode === 'cash' && {
          cashDetails: {
            physicalCash: true,
            cashReceivedFrom: transactionData.cashReceivedFrom || '',
            cashGivenTo: transactionData.cashGivenTo || '',
            cashCountedBy: transactionData.cashCountedBy || '',
            cashVerified: transactionData.cashVerified || false
          }
        }),
        
        // UPI-specific details
        ...(paymentMode === 'upi' && {
          upiDetails: {
            upiId: transactionData.upiId || '',
            transactionId: transactionData.transactionId || '',
            upiApp: transactionData.upiApp || '',
            referenceNumber: transactionData.referenceNumber || '',
            merchantTransactionId: transactionData.merchantTransactionId || '',
            paymentGateway: transactionData.paymentGateway || '',
            screenshotUrl: transactionData.screenshotUrl || '',
            verified: transactionData.verified || false,
            ...(transactionData.verifiedAt && { verifiedAt: transactionData.verifiedAt }),
            ...(transactionData.verifiedBy && { verifiedBy: transactionData.verifiedBy })
          }
        }),
        
        category: transactionData.category || 'General',
        description: transactionData.description || '',
        referenceNumber: transactionData.referenceNumber || '',
        date: transactionData.date || serverTimestamp(),
        status: transactionData.status || 'pending',
        createdBy: transactionData.createdBy || {},
        attachments: transactionData.attachments || [],
        reconciled: false,
        salaryId: transactionData.salaryId || null, // Link to salary payment if applicable
        salaryPaidForEmployeeId: transactionData.salaryPaidForEmployeeId || null, // When paying from other employee's wallet
        salaryPaidForEmployeeName: transactionData.salaryPaidForEmployeeName || null,
        salarySourceType: transactionData.salarySourceType || null, // 'own_wallet' | 'other_employee_wallet'
        linkedPaymentId: transactionData.linkedPaymentId || null, // Link to payment record if applicable
        linkedPaymentType: transactionData.linkedPaymentType || null, // 'purchase' | 'labour' | 'expense' - for cascade and report
        linkedReceiptNumber: transactionData.linkedReceiptNumber || null, // Link to receipt number if applicable
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const transactionRef = await addDoc(
        getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
        transactionRecord
      );
      
      // Update employee balance - if this fails, we still have the transaction record
      // The balance can be recalculated from transactions if needed
      const balanceUpdateResult = await this.updateEmployeeBalance(companyData, employeeId, newBalance);
      
      if (!balanceUpdateResult.success) {
        console.error('Warning: Transaction created but balance update failed:', balanceUpdateResult.error);
        // Transaction is still created, but balance might be out of sync
        // Return success but log the warning
      }
      
      return { 
        success: true, 
        transactionId: transactionRef.id,
        newBalance,
        balanceUpdateSuccess: balanceUpdateResult.success
      };
    } catch (error) {
      console.error('Error creating cash transaction:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Get employee balance
   */
  static async getEmployeeBalance(companyData, employeeId) {
    try {
      const employeeRef = doc(
        db,
        getCollectionForCompany(db, companyData, 'employees').path,
        employeeId
      );
      const employeeSnap = await getDoc(employeeRef);
      
      if (employeeSnap.exists()) {
        const employeeData = employeeSnap.data();
        return employeeData.cashBalance || { total: 0, cash: 0, upi: 0 };
      }
      
      return { total: 0, cash: 0, upi: 0 };
    } catch (error) {
      console.error('Error getting employee balance:', error);
      return { total: 0, cash: 0, upi: 0 };
    }
  }
  
  /**
   * Update employee balance
   */
  static async updateEmployeeBalance(companyData, employeeId, newBalance) {
    try {
      const employeeRef = doc(
        db,
        getCollectionForCompany(db, companyData, 'employees').path,
        employeeId
      );
      
      // First, check if employee exists and get current data
      const employeeSnap = await getDoc(employeeRef);
      
      if (!employeeSnap.exists()) {
        console.error('Employee not found:', employeeId);
        return { success: false, error: 'Employee not found' };
      }
      
      // Ensure cashBalance object exists - use setDoc with merge if needed
      // Update using the full cashBalance object to ensure it's properly set
      const updateData = {
        cashBalance: {
          total: newBalance.total || 0,
          cash: newBalance.cash || 0,
          upi: newBalance.upi || 0,
          lastUpdated: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(employeeRef, updateData);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating employee balance:', error);
      console.error('Error details:', {
        employeeId,
        newBalance,
        errorCode: error.code,
        errorMessage: error.message
      });
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Get employee cash ledger
   */
  static async getEmployeeCashLedger(companyData, employeeId, filters = {}) {
    try {
      let transactionsQuery = query(
        getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
        where('employeeId', '==', employeeId),
        orderBy('date', 'desc')
      );
      
      // Apply filters
      if (filters.paymentMode) {
        transactionsQuery = query(transactionsQuery, where('paymentMode', '==', filters.paymentMode));
      }
      
      if (filters.transactionType) {
        transactionsQuery = query(transactionsQuery, where('transactionType', '==', filters.transactionType));
      }
      
      if (filters.startDate && filters.endDate) {
        transactionsQuery = query(
          transactionsQuery,
          where('date', '>=', filters.startDate),
          where('date', '<=', filters.endDate)
        );
      }
      
      const transactionsSnap = await getDocs(transactionsQuery);
      const transactions = [];
      
      transactionsSnap.forEach(doc => {
        transactions.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort chronologically (date asc, then createdAt asc) so same-day order is correct
      // and "Balance" column (balance after each transaction) reads in narrative order.
      // This avoids confusion when e.g. inflow then outflow on same day: balance after inflow then after outflow.
      const getTime = (tx) => {
        const d = tx.date;
        if (!d) return 0;
        if (d?.toDate) return d.toDate().getTime();
        if (typeof d === 'string') return new Date(d).getTime();
        return 0;
      };
      const getCreated = (tx) => {
        const c = tx.createdAt;
        if (!c) return getTime(tx);
        if (c?.toDate) return c.toDate().getTime();
        if (typeof c === 'string') return new Date(c).getTime();
        return 0;
      };
      transactions.sort((a, b) => {
        const tA = getTime(a), tB = getTime(b);
        if (tA !== tB) return tA - tB;
        return getCreated(a) - getCreated(b);
      });
      
      return { success: true, data: transactions };
    } catch (error) {
      console.error('Error getting employee cash ledger:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Verify UPI transaction
   */
  static async verifyUpiTransaction(companyData, transactionId, verificationData) {
    try {
      const transactionRef = doc(
        db,
        getCollectionForCompany(db, companyData, 'employeeCashTransactions').path,
        transactionId
      );
      
      await updateDoc(transactionRef, {
        'upiDetails.verified': true,
        'upiDetails.verifiedAt': serverTimestamp(),
        'upiDetails.verifiedBy': verificationData.verifiedBy,
        status: 'approved',
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error verifying UPI transaction:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Reconcile employee cash
   */
  static async reconcileEmployeeCash(companyData, employeeId, reconciliationData) {
    try {
      // Get all unreconciled transactions
      const unreconciledQuery = query(
        getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
        where('employeeId', '==', employeeId),
        where('reconciled', '==', false)
      );
      
      const unreconciledSnap = await getDocs(unreconciledQuery);
      const batch = [];
      
      unreconciledSnap.forEach(doc => {
        batch.push(updateDoc(doc.ref, {
          reconciled: true,
          reconciledAt: serverTimestamp(),
          reconciledBy: reconciliationData.reconciledBy,
          updatedAt: serverTimestamp()
        }));
      });
      
      await Promise.all(batch);
      
      return { success: true, reconciledCount: batch.length };
    } catch (error) {
      console.error('Error reconciling employee cash:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Batch-resolve which linked source documents still exist (payments / irregular / salary).
   * Used by reports and summary so totals match the transaction table.
   */
  static async getSourceExistenceSetsForEmployeeTransactions(companyData, transactions) {
    const linkedPaymentIds = new Set();
    const labourLinkedPaymentIds = new Set();
    const expenseLinkedPaymentIds = new Set();
    const salaryIds = new Set();

    for (const t of transactions) {
      if (t.linkedPaymentId) {
        if (t.linkedPaymentType === 'labour') {
          labourLinkedPaymentIds.add(t.linkedPaymentId);
        } else if (t.linkedPaymentType === 'expense') {
          expenseLinkedPaymentIds.add(t.linkedPaymentId);
        } else {
          linkedPaymentIds.add(t.linkedPaymentId);
        }
      }
      if (t.salaryId) {
        salaryIds.add(t.salaryId);
      }
    }

    const existingPaymentIds = new Set();
    if (linkedPaymentIds.size > 0) {
      const paymentsRef = getCollectionForCompany(db, companyData, 'payments');
      const paymentChecks = Array.from(linkedPaymentIds).map(async (paymentId) => {
        try {
          const paymentSnap = await getDoc(doc(paymentsRef, paymentId));
          if (paymentSnap.exists()) return paymentId;
          return null;
        } catch (e) {
          console.error(`Error checking payment ${paymentId}:`, e);
          return null;
        }
      });
      const paymentResults = await Promise.all(paymentChecks);
      paymentResults.forEach(id => { if (id) existingPaymentIds.add(id); });
    }

    const existingLabourPaymentIds = new Set();
    if (labourLinkedPaymentIds.size > 0) {
      const irregularRef = getCollectionForCompany(db, companyData, 'irregularPayments');
      const labourChecks = Array.from(labourLinkedPaymentIds).map(async (irregularId) => {
        try {
          const irregularSnap = await getDoc(doc(irregularRef, irregularId));
          if (irregularSnap.exists()) return irregularId;
          return null;
        } catch (e) {
          console.error(`Error checking labour payment ${irregularId}:`, e);
          return null;
        }
      });
      const labourResults = await Promise.all(labourChecks);
      labourResults.forEach(id => { if (id) existingLabourPaymentIds.add(id); });
    }

    const existingExpenseIds = new Set();
    if (expenseLinkedPaymentIds.size > 0) {
      const expensesRef = getCollectionForCompany(db, companyData, 'expenses');
      const expenseChecks = Array.from(expenseLinkedPaymentIds).map(async (expenseId) => {
        try {
          const expenseSnap = await getDoc(doc(expensesRef, expenseId));
          if (expenseSnap.exists()) return expenseId;
          return null;
        } catch (e) {
          console.error(`Error checking expense ${expenseId}:`, e);
          return null;
        }
      });
      const expenseResults = await Promise.all(expenseChecks);
      expenseResults.forEach(id => { if (id) existingExpenseIds.add(id); });
    }

    const existingSalaryIds = new Set();
    if (salaryIds.size > 0) {
      const salaryRef = getCollectionForCompany(db, companyData, 'salaryPayments');
      const salaryChecks = Array.from(salaryIds).map(async (salaryId) => {
        try {
          const salarySnap = await getDoc(doc(salaryRef, salaryId));
          if (salarySnap.exists()) return salaryId;
          return null;
        } catch (e) {
          console.error(`Error checking salary ${salaryId}:`, e);
          return null;
        }
      });
      const salaryResults = await Promise.all(salaryChecks);
      salaryResults.forEach(id => { if (id) existingSalaryIds.add(id); });
    }

    return { existingPaymentIds, existingLabourPaymentIds, existingExpenseIds, existingSalaryIds };
  }

  /**
   * Whether a wallet row should appear in reporting (source document still exists).
   */
  static isEmployeeCashTransactionReportingValid(transaction, sets) {
    if (transaction.linkedPaymentId) {
      if (transaction.linkedPaymentType === 'labour') {
        if (!sets.existingLabourPaymentIds.has(transaction.linkedPaymentId)) return false;
      } else if (transaction.linkedPaymentType === 'expense') {
        if (!sets.existingExpenseIds.has(transaction.linkedPaymentId)) return false;
      } else if (!sets.existingPaymentIds.has(transaction.linkedPaymentId)) {
        return false;
      }
    }
    if (transaction.salaryId && !sets.existingSalaryIds.has(transaction.salaryId)) {
      return false;
    }
    return true;
  }

  /**
   * Get cash flow summary
   */
  static async getCashFlowSummary(companyData, filters = {}) {
    try {
      let summaryQuery = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      
      if (filters.startDate && filters.endDate) {
        summaryQuery = query(
          summaryQuery,
          where('date', '>=', filters.startDate),
          where('date', '<=', filters.endDate)
        );
      }
      
      const summarySnap = await getDocs(summaryQuery);
      const txRows = summarySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sourceSets = await this.getSourceExistenceSetsForEmployeeTransactions(companyData, txRows);

      const summary = {
        totalInflow: { cash: 0, upi: 0, total: 0 },
        totalOutflow: { cash: 0, upi: 0, total: 0 },
        employees: {}
      };
      
      txRows.forEach(transaction => {
        if (!this.isEmployeeCashTransactionReportingValid(transaction, sourceSets)) {
          return;
        }
        const amount = parseFloat(transaction.amount) || 0;
        
        if (transaction.transactionType === 'inflow') {
          if (transaction.paymentMode === 'cash') {
            summary.totalInflow.cash += amount;
          } else if (transaction.paymentMode === 'upi') {
            summary.totalInflow.upi += amount;
          }
          summary.totalInflow.total += amount;
        } else if (transaction.transactionType === 'outflow') {
          if (transaction.paymentMode === 'cash') {
            summary.totalOutflow.cash += amount;
          } else if (transaction.paymentMode === 'upi') {
            summary.totalOutflow.upi += amount;
          }
          summary.totalOutflow.total += amount;
        }
        
        // Per-employee summary
        if (!summary.employees[transaction.employeeId]) {
          summary.employees[transaction.employeeId] = {
            name: transaction.employeeName,
            inflow: { cash: 0, upi: 0, total: 0 },
            outflow: { cash: 0, upi: 0, total: 0 }
          };
        }
        
        if (transaction.transactionType === 'inflow') {
          if (transaction.paymentMode === 'cash') {
            summary.employees[transaction.employeeId].inflow.cash += amount;
          } else if (transaction.paymentMode === 'upi') {
            summary.employees[transaction.employeeId].inflow.upi += amount;
          }
          summary.employees[transaction.employeeId].inflow.total += amount;
        } else {
          if (transaction.paymentMode === 'cash') {
            summary.employees[transaction.employeeId].outflow.cash += amount;
          } else if (transaction.paymentMode === 'upi') {
            summary.employees[transaction.employeeId].outflow.upi += amount;
          }
          summary.employees[transaction.employeeId].outflow.total += amount;
        }
      });
      
      return { success: true, data: summary };
    } catch (error) {
      console.error('Error getting cash flow summary:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Check if employee has sufficient balance for deduction
   */
  static async canDeductFromBalance(companyData, employeeId, amount, balanceSource = 'both') {
    try {
      const balance = await this.getEmployeeBalance(companyData, employeeId);
      
      if (balanceSource === 'cash') {
        return balance.cash >= amount;
      } else if (balanceSource === 'upi') {
        return balance.upi >= amount;
      } else {
        // 'both' - check total balance
        return balance.total >= amount;
      }
    } catch (error) {
      console.error('Error checking balance:', error);
      return false;
    }
  }
  
  /**
   * Adjust salary from employee cash balance
   * This creates outflow transaction(s) and updates balance
   */
  static async adjustSalaryFromBalance(companyData, salaryData) {
    try {
      const { 
        employeeId, 
        employeeName, 
        amount, 
        balanceSource, 
        salaryId, 
        salaryMonth,
        description,
        date,
        createdBy
      } = salaryData;
      
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return { success: false, error: 'Invalid amount for salary adjustment' };
      }
      
      // Get current balance
      const balance = await this.getEmployeeBalance(companyData, employeeId);
      
      // Determine how to deduct based on balanceSource
      let transactions = [];
      let remainingAmount = amountNum;
      
      if (balanceSource === 'cash') {
        // Deduct only from cash
        if (balance.cash < amountNum) {
          return { success: false, error: `Insufficient cash balance. Available: ₹${balance.cash.toLocaleString()}` };
        }
        
        const transactionResult = await this.createCashTransaction(companyData, {
          employeeId,
          employeeName,
          transactionType: 'outflow',
          amount: amountNum,
          paymentMode: 'cash',
          category: 'Salary',
          description: description || `Salary payment for ${salaryMonth}`,
          date: date || serverTimestamp(),
          salaryId,
          salarySourceType: 'own_wallet',
          createdBy,
          cashGivenTo: 'Salary Payment',
          cashVerified: true
        });
        
        if (!transactionResult.success) {
          return transactionResult;
        }
        
        transactions.push(transactionResult.transactionId);
        
      } else if (balanceSource === 'upi') {
        // Deduct only from UPI
        if (balance.upi < amountNum) {
          return { success: false, error: `Insufficient UPI balance. Available: ₹${balance.upi.toLocaleString()}` };
        }
        
        const transactionResult = await this.createCashTransaction(companyData, {
          employeeId,
          employeeName,
          transactionType: 'outflow',
          amount: amountNum,
          paymentMode: 'upi',
          category: 'Salary',
          description: description || `Salary payment for ${salaryMonth}`,
          date: date || serverTimestamp(),
          salaryId,
          salarySourceType: 'own_wallet',
          createdBy,
          upiId: balance.paymentPreferences?.upiId || '',
          upiApp: balance.paymentPreferences?.upiApp || 'Other',
          verified: true
        });
        
        if (!transactionResult.success) {
          return transactionResult;
        }
        
        transactions.push(transactionResult.transactionId);
        
      } else {
        // 'both' - deduct proportionally or use available balances
        let cashDeduction = 0;
        let upiDeduction = 0;
        
        // First, use available cash
        if (balance.cash > 0 && remainingAmount > 0) {
          cashDeduction = Math.min(balance.cash, remainingAmount);
          remainingAmount -= cashDeduction;
        }
        
        // Then, use available UPI
        if (balance.upi > 0 && remainingAmount > 0) {
          upiDeduction = Math.min(balance.upi, remainingAmount);
          remainingAmount -= upiDeduction;
        }
        
        // Check if we have enough
        if (remainingAmount > 0) {
          return { 
            success: false, 
            error: `Insufficient balance. Available: Cash ₹${balance.cash.toLocaleString()}, UPI ₹${balance.upi.toLocaleString()}` 
          };
        }
        
        // Create cash transaction if needed
        if (cashDeduction > 0) {
          const cashResult = await this.createCashTransaction(companyData, {
            employeeId,
            employeeName,
            transactionType: 'outflow',
            amount: cashDeduction,
            paymentMode: 'cash',
            category: 'Salary',
            description: description || `Salary payment for ${salaryMonth} (Cash)`,
            date: date || serverTimestamp(),
            salaryId,
            salarySourceType: 'own_wallet',
            createdBy,
            cashGivenTo: 'Salary Payment',
            cashVerified: true
          });
          
          if (!cashResult.success) {
            return cashResult;
          }
          
          transactions.push(cashResult.transactionId);
        }
        
        // Create UPI transaction if needed
        if (upiDeduction > 0) {
          const upiResult = await this.createCashTransaction(companyData, {
            employeeId,
            employeeName,
            transactionType: 'outflow',
            amount: upiDeduction,
            paymentMode: 'upi',
            category: 'Salary',
            description: description || `Salary payment for ${salaryMonth} (UPI)`,
            date: date || serverTimestamp(),
            salaryId,
            salarySourceType: 'own_wallet',
            createdBy,
            upiId: balance.paymentPreferences?.upiId || '',
            upiApp: balance.paymentPreferences?.upiApp || 'Other',
            verified: true
          });
          
          if (!upiResult.success) {
            return upiResult;
          }
          
          transactions.push(upiResult.transactionId);
        }
      }
      
      return { 
        success: true, 
        transactionIds: transactions,
        amountDeducted: amountNum
      };
    } catch (error) {
      console.error('Error adjusting salary from balance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Settle adjustable salary amount using other employees' wallets (excluding the salary recipient).
   * Deducts from other employees' balances in order of available balance until amount is fully settled.
   * Creates proper transaction records for reports (category: Salary, salarySourceType: other_employee_wallet).
   */
  static async adjustSalaryFromOtherEmployeesBalance(companyData, salaryData) {
    try {
      const {
        excludeEmployeeDocId,
        salaryEmployeeId: salaryEmployeeDisplayId,
        salaryEmployeeName,
        amount,
        balanceSource,
        salaryId,
        salaryMonth,
        description,
        date,
        createdBy,
        otherEmployees
      } = salaryData;

      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return { success: false, error: 'Invalid amount for salary settlement from other wallets' };
      }

      if (!otherEmployees || otherEmployees.length === 0) {
        return { success: false, error: 'No other employees available to settle from' };
      }

      // Get balances for all other employees and sort by total balance descending (use wallets with most balance first)
      const employeesWithBalance = [];
      for (const emp of otherEmployees) {
        const balance = await this.getEmployeeBalance(companyData, emp.id);
        employeesWithBalance.push({
          id: emp.id,
          name: emp.name || '',
          employeeId: emp.employeeId || '',
          balance
        });
      }
      employeesWithBalance.sort((a, b) => (b.balance.total || 0) - (a.balance.total || 0));

      let remainingAmount = amountNum;
      const transactions = [];
      const deductionLog = [];

      for (const emp of employeesWithBalance) {
        if (remainingAmount <= 0) break;

        const balance = emp.balance;
        let deductCash = 0;
        let deductUpi = 0;

        if (balanceSource === 'cash') {
          deductCash = Math.min(balance.cash || 0, remainingAmount);
          remainingAmount -= deductCash;
        } else if (balanceSource === 'upi') {
          deductUpi = Math.min(balance.upi || 0, remainingAmount);
          remainingAmount -= deductUpi;
        } else {
          // 'both' - use cash first, then UPI
          deductCash = Math.min(balance.cash || 0, remainingAmount);
          remainingAmount -= deductCash;
          if (remainingAmount > 0) {
            deductUpi = Math.min(balance.upi || 0, remainingAmount);
            remainingAmount -= deductUpi;
          }
        }

        const descBase = description || `Salary payment for ${salaryMonth}`;
        const forRecipient = ` for ${salaryEmployeeName}`;
        const fromWallet = ` (from ${emp.name}'s wallet)`;

        if (deductCash > 0) {
          const cashResult = await this.createCashTransaction(companyData, {
            employeeId: emp.id,
            employeeName: emp.name,
            transactionType: 'outflow',
            amount: deductCash,
            paymentMode: 'cash',
            category: 'Salary',
            description: `${descBase}${forRecipient}${fromWallet} (Cash)`,
            date: date || serverTimestamp(),
            salaryId,
            salaryPaidForEmployeeId: salaryEmployeeDisplayId,
            salaryPaidForEmployeeName: salaryEmployeeName,
            salarySourceType: 'other_employee_wallet',
            createdBy,
            cashGivenTo: `Salary payment for ${salaryEmployeeName}`,
            cashVerified: true
          });

          if (!cashResult.success) {
            console.error('[Salary other wallets] Cash deduction failed for', emp.name, cashResult.error);
            return cashResult;
          }
          transactions.push(cashResult.transactionId);
          deductionLog.push({ employeeName: emp.name, amount: deductCash, mode: 'cash' });
        }

        if (deductUpi > 0) {
          const upiResult = await this.createCashTransaction(companyData, {
            employeeId: emp.id,
            employeeName: emp.name,
            transactionType: 'outflow',
            amount: deductUpi,
            paymentMode: 'upi',
            category: 'Salary',
            description: `${descBase}${forRecipient}${fromWallet} (UPI)`,
            date: date || serverTimestamp(),
            salaryId,
            salaryPaidForEmployeeId: salaryEmployeeDisplayId,
            salaryPaidForEmployeeName: salaryEmployeeName,
            salarySourceType: 'other_employee_wallet',
            createdBy,
            upiId: emp.upiId || '',
            upiApp: emp.upiApp || 'Other',
            verified: true
          });

          if (!upiResult.success) {
            console.error('[Salary other wallets] UPI deduction failed for', emp.name, upiResult.error);
            return upiResult;
          }
          transactions.push(upiResult.transactionId);
          deductionLog.push({ employeeName: emp.name, amount: deductUpi, mode: 'upi' });
        }
      }

      if (remainingAmount > 0) {
        const totalAvailable = amountNum - remainingAmount;
        console.warn('[Salary other wallets] Insufficient combined balance', {
          requested: amountNum,
          settled: totalAvailable,
          shortfall: remainingAmount
        });
        return {
          success: false,
          error: `Insufficient combined balance in other employees' wallets. Settled ₹${totalAvailable.toLocaleString('en-IN')}, shortfall ₹${remainingAmount.toLocaleString('en-IN')}.`
        };
      }

      console.log('[Salary other wallets] Settlement complete', {
        salaryId,
        salaryMonth,
        salaryEmployeeName,
        amountDeducted: amountNum,
        transactionIds: transactions,
        deductionLog
      });

      return {
        success: true,
        transactionIds: transactions,
        amountDeducted: amountNum,
        deductionLog
      };
    } catch (error) {
      console.error('Error adjusting salary from other employees balance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Settle salary amount from a SPECIFIED employee's wallet (not auto-distributed across multiple).
   * Deducts from the chosen employee's balance based on available Cash/UPI/Both.
   */
  static async adjustSalaryFromSpecifiedEmployeeWallet(companyData, salaryData) {
    try {
      const {
        sourceEmployeeId,
        sourceEmployeeName,
        salaryEmployeeId: salaryEmployeeDisplayId,
        salaryEmployeeName,
        amount,
        balanceSource,
        salaryId,
        salaryMonth,
        description,
        date,
        createdBy,
        sourceEmployee
      } = salaryData;

      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return { success: false, error: 'Invalid amount for salary settlement' };
      }

      if (!sourceEmployeeId || !sourceEmployee) {
        return { success: false, error: 'Source employee (wallet) is required' };
      }

      const balance = await this.getEmployeeBalance(companyData, sourceEmployeeId);
      let deductCash = 0;
      let deductUpi = 0;

      if (balanceSource === 'cash') {
        deductCash = Math.min(balance.cash || 0, amountNum);
        if (deductCash < amountNum) {
          return {
            success: false,
            error: `Insufficient Cash balance. Available: ₹${(balance.cash || 0).toLocaleString('en-IN')}, required: ₹${amountNum.toLocaleString('en-IN')}.`
          };
        }
      } else if (balanceSource === 'upi') {
        deductUpi = Math.min(balance.upi || 0, amountNum);
        if (deductUpi < amountNum) {
          return {
            success: false,
            error: `Insufficient UPI balance. Available: ₹${(balance.upi || 0).toLocaleString('en-IN')}, required: ₹${amountNum.toLocaleString('en-IN')}.`
          };
        }
      } else {
        // 'both' - use cash first, then UPI
        const cashAvailable = balance.cash || 0;
        const upiAvailable = balance.upi || 0;
        const totalAvailable = cashAvailable + upiAvailable;
        if (totalAvailable < amountNum) {
          return {
            success: false,
            error: `Insufficient combined balance. Available: ₹${totalAvailable.toLocaleString('en-IN')} (Cash: ₹${cashAvailable.toLocaleString('en-IN')}, UPI: ₹${upiAvailable.toLocaleString('en-IN')}), required: ₹${amountNum.toLocaleString('en-IN')}.`
          };
        }
        deductCash = Math.min(cashAvailable, amountNum);
        const remaining = amountNum - deductCash;
        deductUpi = Math.min(upiAvailable, remaining);
      }

      const descBase = description || `Salary payment for ${salaryMonth}`;
      const forRecipient = ` for ${salaryEmployeeName}`;
      const fromWallet = ` (from ${sourceEmployeeName}'s wallet)`;
      const transactions = [];
      const deductionLog = [];

      if (deductCash > 0) {
        const cashResult = await this.createCashTransaction(companyData, {
          employeeId: sourceEmployeeId,
          employeeName: sourceEmployeeName,
          transactionType: 'outflow',
          amount: deductCash,
          paymentMode: 'cash',
          category: 'Salary',
          description: `${descBase}${forRecipient}${fromWallet} (Cash)`,
          date: date || serverTimestamp(),
          salaryId,
          salaryPaidForEmployeeId: salaryEmployeeDisplayId,
          salaryPaidForEmployeeName: salaryEmployeeName,
          salarySourceType: 'other_employee_wallet',
          createdBy,
          cashGivenTo: `Salary payment for ${salaryEmployeeName}`,
          cashVerified: true
        });

        if (!cashResult.success) return cashResult;
        transactions.push(cashResult.transactionId);
        deductionLog.push({ employeeName: sourceEmployeeName, amount: deductCash, mode: 'cash' });
      }

      if (deductUpi > 0) {
        const upiResult = await this.createCashTransaction(companyData, {
          employeeId: sourceEmployeeId,
          employeeName: sourceEmployeeName,
          transactionType: 'outflow',
          amount: deductUpi,
          paymentMode: 'upi',
          category: 'Salary',
          description: `${descBase}${forRecipient}${fromWallet} (UPI)`,
          date: date || serverTimestamp(),
          salaryId,
          salaryPaidForEmployeeId: salaryEmployeeDisplayId,
          salaryPaidForEmployeeName: salaryEmployeeName,
          salarySourceType: 'other_employee_wallet',
          createdBy,
          upiId: sourceEmployee?.upiId || '',
          upiApp: sourceEmployee?.upiApp || 'Other',
          verified: true
        });

        if (!upiResult.success) return upiResult;
        transactions.push(upiResult.transactionId);
        deductionLog.push({ employeeName: sourceEmployeeName, amount: deductUpi, mode: 'upi' });
      }

      return {
        success: true,
        transactionIds: transactions,
        amountDeducted: amountNum,
        deductionLog
      };
    } catch (error) {
      console.error('Error adjusting salary from specified employee wallet:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Validate UPI ID format
   */
  static validateUpiId(upiId) {
    if (!upiId) return { valid: false, error: 'UPI ID is required' };
    
    // UPI ID format: username@provider (e.g., user@paytm, user@ybl)
    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
    if (!upiRegex.test(upiId)) {
      return { valid: false, error: 'Invalid UPI ID format. Expected: username@provider' };
    }
    
    return { valid: true };
  }
  
  /**
   * Validate transaction amount
   */
  static validateAmount(amount) {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return { valid: false, error: 'Amount must be a positive number' };
    }
    return { valid: true };
  }

  /**
   * Delete cash transaction and reverse balance effect
   */
  static async deleteCashTransaction(companyData, transactionId) {
    try {
      // Get the transaction document first
      const transactionRef = doc(
        getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
        transactionId
      );
      const transactionSnap = await getDoc(transactionRef);
      
      if (!transactionSnap.exists()) {
        return { success: false, error: 'Transaction not found' };
      }
      
      const transactionData = transactionSnap.data();
      const { employeeId, transactionType, amount, paymentMode } = transactionData;
      
      // Validate transaction data
      if (!employeeId || !transactionType || !amount || !paymentMode) {
        return { success: false, error: 'Invalid transaction data' };
      }
      
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        return { success: false, error: 'Invalid transaction amount' };
      }
      
      // Get current employee balance
      const currentBalance = await this.getEmployeeBalance(companyData, employeeId);
      
      // Calculate reversed balance (opposite of original transaction)
      let reversedBalance = {
        total: currentBalance.total || 0,
        cash: currentBalance.cash || 0,
        upi: currentBalance.upi || 0
      };
      
      // Reverse the transaction effect
      if (transactionType === 'inflow') {
        // Original was inflow (added money), so reverse by subtracting
        if (paymentMode === 'cash') {
          reversedBalance.cash -= amountNum;
          reversedBalance.total -= amountNum;
        } else if (paymentMode === 'upi') {
          reversedBalance.upi -= amountNum;
          reversedBalance.total -= amountNum;
        }
      } else if (transactionType === 'outflow') {
        // Original was outflow (subtracted money), so reverse by adding
        if (paymentMode === 'cash') {
          reversedBalance.cash += amountNum;
          reversedBalance.total += amountNum;
        } else if (paymentMode === 'upi') {
          reversedBalance.upi += amountNum;
          reversedBalance.total += amountNum;
        }
      }
      
      // Validate reversed balance (should not go negative for cash/upi individually)
      if (reversedBalance.cash < 0 || reversedBalance.upi < 0) {
        console.warn('Warning: Reversing transaction would result in negative balance. Proceeding anyway.');
      }
      
      // Update employee balance first (before deleting transaction)
      const balanceUpdateResult = await this.updateEmployeeBalance(companyData, employeeId, reversedBalance);
      
      if (!balanceUpdateResult.success) {
        return { 
          success: false, 
          error: `Failed to update balance: ${balanceUpdateResult.error}` 
        };
      }
      
      // Delete the transaction document
      await deleteDoc(transactionRef);
      
      return { 
        success: true, 
        newBalance: reversedBalance,
        message: 'Transaction deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting cash transaction:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete employee cash transactions linked to a document in `payments` (party receipts, etc.).
   * Skips `linkedPaymentType === 'labour'` rows (those use `irregularPayments` ids).
   */
  static async deleteTransactionsForPaymentsCollectionPayment(companyData, paymentId) {
    try {
      if (!paymentId) return { success: true, deletedCount: 0 };

      const txRef = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      if (!txRef) {
        return { success: false, deletedCount: 0, error: 'Invalid company/collection path' };
      }

      const q = query(txRef, where('linkedPaymentId', '==', paymentId));
      const snap = await getDocs(q);

      const results = [];
      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        if (data.linkedPaymentType === 'labour' || data.linkedPaymentType === 'expense') continue;

        const result = await this.deleteCashTransaction(companyData, docSnap.id);
        results.push({ id: docSnap.id, ...result });
      }

      const successCount = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        return {
          success: false,
          deletedCount: successCount,
          error: failed.map(f => f.error).join('; ')
        };
      }

      return { success: true, deletedCount: successCount };
    } catch (err) {
      console.error('Error deleting transactions for payment:', err);
      return { success: false, deletedCount: 0, error: err.message };
    }
  }

  /**
   * Delete all employee cash transactions linked to a salary payment.
   * Use when deleting a salary payment that had wallet settlements (own or other employee).
   * Each transaction is properly reversed (balance updated) before deletion.
   */
  static async deleteTransactionsForSalary(companyData, salaryId) {
    try {
      if (!salaryId) return { success: true, deletedCount: 0 };

      const txRef = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      if (!txRef) {
        console.error('[deleteTransactionsForSalary] Invalid collection ref - companyData:', companyData);
        return { success: false, deletedCount: 0, error: 'Invalid company/collection path' };
      }

      const q = query(txRef, where('salaryId', '==', salaryId));
      const snap = await getDocs(q);
      const foundCount = snap.docs.length;
      if (foundCount > 0) {
        console.log(`[deleteTransactionsForSalary] Found ${foundCount} transaction(s) for salaryId: ${salaryId}`);
      }

      const results = [];
      for (const docSnap of snap.docs) {
        const result = await this.deleteCashTransaction(companyData, docSnap.id);
        results.push({ id: docSnap.id, ...result });
      }

      const successCount = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        console.warn('[deleteTransactionsForSalary] Some deletions failed:', failed);
        return {
          success: false,
          deletedCount: successCount,
          error: failed.map(f => f.error).join('; ')
        };
      }

      return { success: true, deletedCount: successCount };
    } catch (err) {
      console.error('Error deleting transactions for salary:', err);
      return { success: false, deletedCount: 0, error: err.message };
    }
  }

  /**
   * Delete all employee cash transactions linked to a labour/irregular payment.
   * Use when deleting a labour/advance entry that had wallet recording or settlement.
   */
  /**
   * Delete all employee cash transactions linked to a fixed/variable expense wallet settlement.
   */
  static async deleteTransactionsForExpense(companyData, expenseId) {
    try {
      if (!expenseId) return { success: true, deletedCount: 0 };

      const txRef = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      if (!txRef) {
        return { success: false, deletedCount: 0, error: 'Invalid company/collection path' };
      }

      const q = query(
        txRef,
        where('linkedPaymentId', '==', expenseId),
        where('linkedPaymentType', '==', 'expense')
      );
      const snap = await getDocs(q);

      const results = [];
      for (const docSnap of snap.docs) {
        const result = await this.deleteCashTransaction(companyData, docSnap.id);
        results.push({ id: docSnap.id, ...result });
      }

      const successCount = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        return {
          success: false,
          deletedCount: successCount,
          error: failed.map(f => f.error).join('; ')
        };
      }

      return { success: true, deletedCount: successCount };
    } catch (err) {
      console.error('Error deleting transactions for expense:', err);
      return { success: false, deletedCount: 0, error: err.message };
    }
  }

  static async deleteTransactionsForIrregularPayment(companyData, irregularPaymentId) {
    try {
      if (!irregularPaymentId) return { success: true, deletedCount: 0 };

      const txRef = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      if (!txRef) {
        console.error('[deleteTransactionsForIrregularPayment] Invalid collection ref - companyData:', companyData);
        return { success: false, deletedCount: 0, error: 'Invalid company/collection path' };
      }

      const q = query(
        txRef,
        where('linkedPaymentId', '==', irregularPaymentId),
        where('linkedPaymentType', '==', 'labour')
      );
      const snap = await getDocs(q);
      const foundCount = snap.docs.length;

      const results = [];
      for (const docSnap of snap.docs) {
        const result = await this.deleteCashTransaction(companyData, docSnap.id);
        results.push({ id: docSnap.id, ...result });
      }

      const successCount = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        console.warn('[deleteTransactionsForIrregularPayment] Some deletions failed:', failed);
        return {
          success: false,
          deletedCount: successCount,
          error: failed.map(f => f.error).join('; ')
        };
      }

      return { success: true, deletedCount: successCount };
    } catch (err) {
      console.error('Error deleting transactions for irregular payment:', err);
      return { success: false, deletedCount: 0, error: err.message };
    }
  }

  /**
   * Explain why a row is treated as orphaned (for UI / logs).
   */
  static orphanReasonLabel(transaction, sets) {
    if (transaction.linkedPaymentId) {
      if (transaction.linkedPaymentType === 'labour') {
        if (!sets.existingLabourPaymentIds.has(transaction.linkedPaymentId)) {
          return 'Linked irregular/labour payment was deleted';
        }
      } else if (transaction.linkedPaymentType === 'expense') {
        if (!sets.existingExpenseIds.has(transaction.linkedPaymentId)) {
          return 'Linked expense was deleted';
        }
      } else if (!sets.existingPaymentIds.has(transaction.linkedPaymentId)) {
        return 'Linked party payment was deleted';
      }
    }
    if (transaction.salaryId && !sets.existingSalaryIds.has(transaction.salaryId)) {
      return 'Linked salary payment was deleted';
    }
    return 'Source document missing';
  }

  /**
   * Full scan: list employee wallet rows whose linked payment / salary / irregular doc no longer exists.
   */
  static async findOrphanEmployeeCashTransactions(companyData) {
    try {
      const txRef = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
      if (!txRef) {
        return { success: false, orphans: [], error: 'Invalid company/collection path' };
      }

      const snap = await getDocs(query(txRef));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sets = await this.getSourceExistenceSetsForEmployeeTransactions(companyData, rows);
      const orphans = [];

      for (const t of rows) {
        if (!this.isEmployeeCashTransactionReportingValid(t, sets)) {
          orphans.push({
            id: t.id,
            employeeId: t.employeeId,
            employeeName: t.employeeName || '',
            amount: t.amount,
            date: t.date,
            description: t.description || '',
            transactionType: t.transactionType,
            paymentMode: t.paymentMode,
            linkedPaymentId: t.linkedPaymentId || null,
            linkedPaymentType: t.linkedPaymentType || null,
            salaryId: t.salaryId || null,
            linkedReceiptNumber: t.linkedReceiptNumber || null,
            reason: this.orphanReasonLabel(t, sets)
          });
        }
      }

      return { success: true, orphans, count: orphans.length };
    } catch (err) {
      console.error('findOrphanEmployeeCashTransactions:', err);
      return { success: false, orphans: [], error: err.message };
    }
  }

  /**
   * Reverse balances and delete every orphan found by findOrphanEmployeeCashTransactions.
   */
  static async repairOrphanEmployeeCashTransactions(companyData) {
    const scan = await this.findOrphanEmployeeCashTransactions(companyData);
    if (!scan.success) {
      return { success: false, repairedCount: 0, failed: [], error: scan.error };
    }

    const failed = [];
    let repairedCount = 0;
    for (const o of scan.orphans) {
      const res = await this.deleteCashTransaction(companyData, o.id);
      if (res.success) {
        repairedCount += 1;
      } else {
        failed.push({ id: o.id, error: res.error });
      }
    }

    return {
      success: failed.length === 0,
      repairedCount,
      failed,
      error: failed.length ? failed.map(f => `${f.id}: ${f.error}`).join('; ') : undefined
    };
  }
}

export default EmployeeCashManager;

